import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import PropTypes from "prop-types";
import React, { useState } from "react";
import styled from "styled-components";
import InternalLink from "components/InternalLink";
import { useGhostSettings } from "app/settings";

const HeaderContainer = styled.header`
    background: #8acdd6;
    ${({ burgerOpen }) =>
        burgerOpen &&
        `
        bottom: 0;
        left: 0;
        overflow-y: scroll;
        position: fixed;
        right: 0;
        top: 0;
        z-index: 3999999;
    `}
    ${({ isHeadTransparent }) =>
        isHeadTransparent &&
        `
        background-color: transparent;
        left: 0;
        position: absolute;
        right: 0;
        z-index: 90;
    `}
`;

const HeaderInner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 36px;
    color: #ffffff;
    background-position: center;
    background-size: cover;
    display: grid;
    grid-auto-flow: row dense;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    @media (max-width: 991px) {
        grid-template-columns: 1fr;
        padding: 32px 36px;
        ${({ burgerOpen }) =>
            burgerOpen &&
            `grid-template-rows: auto 1fr auto;
            height: 100%;`}
    }
`;

const HeaderDescription = styled(InternalLink)`
    position: relative;
    color: #ffffff;
    display: flex;
    align-items: center;
    &:hover {
        text-decoration: none;
    }
    @media (max-width: 991px) {
        display: none;
    }
`;

const HeaderBrand = styled.div`
    display: flex;
    align-items: center;
    height: 80px;
    display: flex;
    max-width: 400px;
    word-break: break-all;
    @media (max-width: 991px) {
        align-items: center;
        display: flex;
        grid-column-start: 1;
        justify-content: space-between;
        max-width: 100%;
        height: 40px;
    }
`;

const HeaderLogo = styled.img`
    max-height: 32px;
`;

const HeaderMenu = styled.nav`
    width: 100%;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: 1/4;
    margin-top: 1px;
    font-weight: 500;
    color: #ffffff;
    &::after {
        position: absolute;
        top: 80px;
        left: 0;
        width: 100%;
        height: 1px;
        content: "";
        background-color: #ffffff4d;
    }
    @media (max-width: 991px) {
        display: ${({ burgerOpen }) => (burgerOpen ? "block" : "none")};
        height: max-content;
        grid-row-start: 2;
        text-align: center;
        &::after {
            display: none;
        }
    }
`;
const HeaderNav = styled.ul`
    margin: 0;
    @media (max-width: 991px) {
        display: flex;
        flex-direction: column;
    }
`;
const HeaderNavItem = styled(InternalLink)`
    display: inline-block;
    padding: 5px 16px;
    color: #ffffff;
    font-size: 14px;
    &:hover {
        text-decoration: none;
        opacity: 0.7;
    }
    @media (max-width: 991px) {
        font-size: 2.4rem;
        margin-top: 1.2rem;
        font-weight: 400;
        letter-spacing: -0.006em;
        line-height: 1.5;
    }
`;

const Burger = styled.button`
    width: 30px;
    height: 30px;
    position: relative;
    display: none;
    background-color: transparent;
    cursor: pointer;
    @media (max-width: 991px) {
        display: block;
    }
`;

const BurgerLine = styled.div`
    position: absolute;
    width: 24px;
    height: 1px;
    background-color: #ffffff;
    left: 0;
    right: 0;
    top: 50%;
    ${({ isFirst }) => isFirst && `transform: translateY(-6px);`}
    transition: 0.1s linear;
    @media (max-width: 991px) {
        ${({ burgerOpen, isFirst }) =>
            burgerOpen && `transform: rotate(${isFirst ? "45deg" : "-45deg"})`}
    }
`;

const Header = ({ isHeadTransparent }) => {
    const site = useGhostSettings();
    const image = useStaticQuery(graphql`
        query GhostSettings {
            file(relativePath: { eq: "ghost-icon.png" }) {
                childImageSharp {
                    gatsbyImageData(width: 30, height: 30, layout: FIXED)
                }
            }
        }
    `).file.childImageSharp.gatsbyImageData;
    const [burgerOpen, setBurgerOpen] = useState(false);

    return (
        <HeaderContainer
            burgerOpen={burgerOpen}
            isHeadTransparent={isHeadTransparent}
        >
            <HeaderInner burgerOpen={burgerOpen}>
                <HeaderDescription to="/">
                    <span>{site.description}</span>
                </HeaderDescription>
                <HeaderBrand>
                    <InternalLink to="/">
                        {site.logo ? (
                            <HeaderLogo
                                src={
                                    isHeadTransparent
                                        ? "/logo_820_312.png"
                                        : site.logo
                                }
                                alt={site.title}
                            />
                        ) : (
                            <GatsbyImage image={image} alt={site.title} />
                        )}
                    </InternalLink>
                    <Burger onClick={() => setBurgerOpen(!burgerOpen)}>
                        <BurgerLine isFirst burgerOpen={burgerOpen} />
                        <BurgerLine burgerOpen={burgerOpen} />
                    </Burger>
                </HeaderBrand>
                <HeaderMenu burgerOpen={burgerOpen}>
                    <HeaderNav>
                        {/* The navigation items as setup in Ghost */}
                        {site.navigation.map((nav) => (
                            <HeaderNavItem to={nav.url} key={nav.label}>
                                {nav.label}
                            </HeaderNavItem>
                        ))}
                    </HeaderNav>
                </HeaderMenu>
            </HeaderInner>
        </HeaderContainer>
    );
};

Header.propTypes = {
    isHeadTransparent: PropTypes.bool,
};

export default Header;
